'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { WHY_MENU } from './nav-links'
import type { ChromeNavMenu } from './nav-links'

const CLOSE_DELAY_MS = 150

interface NavDropdownProps {
  /** Menu to render — defaults to the "Why PrepSolution" menu. */
  menu?: ChromeNavMenu
  /** Unique id for the popup panel (aria-controls target). */
  panelId?: string
}

/**
 * Desktop dropdown for a small link menu (Resources, Why PrepSolution).
 * Mirrors the ExamsMegaPanel interaction model — hover-open with a short
 * close delay, click toggle, Escape closes and refocuses the trigger, outside
 * pointerdown and blur-out close — but the panel anchors to the trigger
 * itself rather than the navbar island.
 */
export function NavDropdown({
  menu = WHY_MENU,
  panelId = 'why-prepsolution-menu',
}: NavDropdownProps) {
  const [open, setOpen] = useState<boolean>(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const closeTimer = useRef<number | null>(null)

  useEffect(() => {
    if (!open) return
    const handlePointerDown = (event: MouseEvent): void => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      const focusWasInside =
        wrapperRef.current?.contains(document.activeElement) ?? false
      setOpen(false)
      if (focusWasInside) triggerRef.current?.focus()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open])

  useEffect(() => {
    return () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    }
  }, [])

  const cancelClose = (): void => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  const scheduleClose = (): void => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS)
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onPointerEnter={(event: React.PointerEvent) => {
        if (event.pointerType === 'mouse') {
          cancelClose()
          setOpen(true)
        }
      }}
      onMouseLeave={scheduleClose}
      onBlur={(event) => {
        if (!wrapperRef.current?.contains(event.relatedTarget as Node)) {
          setOpen(false)
        }
      }}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest('a')) setOpen(false)
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen(!open)}
        className="text-body hover:text-ink hover:bg-ink/[0.05] flex h-10 items-center gap-1 rounded-full px-3 text-sm font-medium transition-colors"
      >
        {menu.label}
        <ChevronDown
          size={14}
          className={cn(
            'transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>
      {open && (
        <div className="absolute top-full left-0 w-72 pt-2">
          <div
            id={panelId}
            className="border-border bg-bg shadow-float rounded-2xl border p-1.5"
          >
            {menu.items.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="hover:bg-surface flex flex-col rounded-xl px-3 py-2.5 transition-colors"
              >
                <span className="text-ink text-sm font-semibold">
                  {item.label}
                </span>
                <span className="text-hint text-[13px]">
                  {item.description}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
